import './coapplicantEmploymentDetailsPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { inputEventHandler } from '../../../utils/component/inputEventHandler';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentIncomeEmployerName,
    coapplicantCurrentIncomeGrossAnnual,
    coapplicantCurrentIncomeJobTitle,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentIncomeEmployerName,
    setCoapplicantCurrentIncomeGrossAnnual,
    setCoapplicantCurrentIncomeJobTitle,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    defaultValues: {
      employerNameValue: coapplicantCurrentIncomeEmployerName,
      jobTitleValue: coapplicantCurrentIncomeJobTitle,
      grossAnnualIncomeValue: inputEventHandler.currency.formatter(
        coapplicantCurrentIncomeGrossAnnual,
      ),
    },
    delayError: 200,
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const employerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const jobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { employerNameValue, jobTitleValue, grossAnnualIncomeValue } = values;

    if (!employerNameValue || !jobTitleValue || !grossAnnualIncomeValue) {
      return;
    }

    setCoapplicantCurrentIncomeEmployerName(employerNameValue);
    setCoapplicantCurrentIncomeGrossAnnual(grossAnnualIncomeValue);
    setCoapplicantCurrentIncomeJobTitle(jobTitleValue);

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-employment-details-page"
        sx={theme.pageLayout}
      >
        <header
          data-testid={locator.pages.coapplicantEmploymentDetails.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantEmploymentDetails.header
                .previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantEmploymentDetails.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={
              locator.pages.coapplicantEmploymentDetails.progress.root
            }
            value={useWorkflowStore.getState().computed.progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantEmploymentDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.coapplicantEmploymentDetails.form.h1}
              >
                {t('CoapplicantEmploymentDetailsPage.title')}
              </h1>
              <h2
                data-testid={locator.pages.coapplicantEmploymentDetails.form.h2}
              >
                {t('CoapplicantEmploymentDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEmploymentDetails.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register('employerNameValue', employerNameValidation)}
                    error={!isEmpty(errors.employerNameValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantEmploymentDetails.form
                          .employerNameInput,
                    }}
                    label={t('CoapplicantEmploymentDetailsPage.employerName')}
                    variant="outlined"
                    fullWidth
                  />

                  {!isEmpty(errors.employerNameValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEmploymentDetails.form
                          .employerNameInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.employerNameValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('jobTitleValue', jobTitleValidation)}
                    error={!isEmpty(errors.jobTitleValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantEmploymentDetails.form
                          .jobTitleInput,
                    }}
                    label={t('CoapplicantEmploymentDetailsPage.jobTitle')}
                    variant="outlined"
                    fullWidth
                  />

                  {!isEmpty(errors.jobTitleValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEmploymentDetails.form
                          .jobTitleInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.jobTitleValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register(
                      'grossAnnualIncomeValue',
                      grossAnnualIncomeValidation,
                    )}
                    error={!isEmpty(errors.grossAnnualIncomeValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.coapplicantEmploymentDetails.form
                          .grossAnnualIncomeInput,
                      inputMode: 'numeric',
                    }}
                    label={t(
                      'CoapplicantEmploymentDetailsPage.grossAnnualIncome',
                    )}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'grossAnnualIncomeValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'grossAnnualIncomeValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'grossAnnualIncomeValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.grossAnnualIncomeValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEmploymentDetails.form
                          .grossAnnualIncomeInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.grossAnnualIncomeValue.message)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantEmploymentDetails.form.submitButton
                  }
                  disabled={!isValid}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantEmploymentDetailsPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantEmploymentDetails.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEmploymentDetailsPage';
